import {
    TextEditor,
    type TextEditorSize,
    type TextEditorToolbar,
    type TextEditorType,
} from '@trustoo/components/TextEditor/v2'
import React, { useState } from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const TEXT_EDITOR_USAGE = `import { TextEditor } from '@trustoo/components/TextEditor/v2'

const [html, setHtml] = useState('<p>Hello</p>')

<TextEditor
    label="Description"
    value={html}
    onChange={setHtml}
    maxLength={1000}
    onRequestLink={() => window.prompt('Link URL')}
/>

// Bubble menu over the selection instead of a fixed toolbar
<TextEditor type="floating-toolbar" toolbar="simple" defaultValue={html} />`

const SAMPLE_HTML =
    '<p>We need another and a wiser and perhaps a more <strong>mystical</strong> concept of animals.</p><ul><li>Select some text to try the toolbar.</li></ul>'

const promptForUrl = (label: string) => () => window.prompt(label)

const StatefulTextEditor: React.FC<{
    type?: TextEditorType
    toolbar?: TextEditorToolbar
    size?: TextEditorSize
    maxLength?: number
    showHtml?: boolean
}> = ({ type, toolbar, size, maxLength, showHtml = false }) => {
    const [html, setHtml] = useState(SAMPLE_HTML)
    return (
        <div className="space-y-spacing-md">
            <TextEditor
                label="Description"
                value={html}
                onChange={setHtml}
                type={type}
                toolbar={toolbar}
                size={size}
                maxLength={maxLength}
                placeholder="Write something…"
                onRequestLink={promptForUrl('Link URL')}
                onRequestImage={promptForUrl('Image URL')}
            />
            {showHtml && <CodeSnippet code={html} />}
        </div>
    )
}

export const TextEditorShowcase: React.FC = () => (
    <Section
        title="TextEditor (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Text editor</span> set
                (ComponentSet <InlineCode>5:43808</InlineCode>). A rich-text editor built on{' '}
                <InlineCode>Tiptap</InlineCode> that reads and writes HTML. The font selects in the
                advanced toolbar <span className="font-semibold">compose</span>{' '}
                <InlineCode>Select/v2</InlineCode>. Import via{' '}
                <InlineCode>@trustoo/components/TextEditor/v2</InlineCode>; apps other than office
                must install the Tiptap peer dependencies first.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">
                    Default (fixed advanced toolbar)
                </h3>
                <p className="text-xs text-text-tertiary">
                    Link and image buttons call <InlineCode>onRequestLink</InlineCode> /{' '}
                    <InlineCode>onRequestImage</InlineCode>; the HTML below updates through{' '}
                    <InlineCode>onChange</InlineCode>.
                </p>
                <div className="rounded-radius-xs border border-border-secondary bg-background-bg-secondary p-spacing-xl">
                    <StatefulTextEditor showHtml />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Toolbars</h3>
                <div className="grid grid-cols-1 gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-background-bg-secondary p-spacing-xl lg:grid-cols-2">
                    <div className="space-y-spacing-md">
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            toolbar=&quot;simple&quot;
                        </span>
                        <StatefulTextEditor toolbar="simple" />
                    </div>
                    <div className="space-y-spacing-md">
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            type=&quot;floating-toolbar&quot;
                        </span>
                        <StatefulTextEditor type="floating-toolbar" />
                    </div>
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">
                    Sizes and <InlineCode>maxLength</InlineCode>
                </h3>
                <div className="grid grid-cols-1 gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-background-bg-secondary p-spacing-xl lg:grid-cols-2">
                    <div className="space-y-spacing-md">
                        <span className="text-xs font-medium uppercase text-text-tertiary">sm</span>
                        <StatefulTextEditor size="sm" toolbar="simple" maxLength={500} />
                    </div>
                    <div className="space-y-spacing-md">
                        <span className="text-xs font-medium uppercase text-text-tertiary">md</span>
                        <StatefulTextEditor size="md" toolbar="simple" maxLength={500} />
                    </div>
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={TEXT_EDITOR_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>value</InlineCode> / <InlineCode>defaultValue</InlineCode> +{' '}
                    <InlineCode>onChange</InlineCode> (HTML), <InlineCode>type</InlineCode> (default
                    · floating-toolbar), <InlineCode>toolbar</InlineCode> (simple · advanced),{' '}
                    <InlineCode>size</InlineCode> (sm · md), <InlineCode>label</InlineCode>,{' '}
                    <InlineCode>placeholder</InlineCode>, <InlineCode>hintText</InlineCode>,{' '}
                    <InlineCode>maxLength</InlineCode>, <InlineCode>disabled</InlineCode>,{' '}
                    <InlineCode>onRequestLink</InlineCode>, <InlineCode>onRequestImage</InlineCode>{' '}
                    and <InlineCode>onGenerate</InlineCode>. Buttons whose callback isn&apos;t
                    passed are hidden.
                </p>
            </div>
        </div>
    </Section>
)
